import React from 'react';

import i18n from 'util/i18n';

import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import DateControl from 'components/Form/DateControl';
import Text from 'components/Form/Text';
import Textarea from 'components/Form/Textarea';
import Branch from 'components/Form/Branch';
import { CommonOnUpdate, CommonOnError, CommonRequired } from 'types/props';
import { SubstanceThcInvolvementItemModel } from 'forms/sections/substance/thc/purchase/types';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import { AccordionItemProps } from 'components/Form/Accordion/types';
import Dropdown from 'components/Form/Dropdown';
import { criminalJusticeorPublicSafetyPosition } from 'constants/enums/substanceOptions';

interface Props
  extends Partial<SubstanceThcInvolvementItemModel>,
  ValidationElementProps,
  AccordionItemProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: boolean
  formType?: string
}

export default class ThcInvolvement extends ValidationElement<Props> {
  update = (values: Partial<SubstanceThcInvolvementItemModel>) => {
    if (this.props.onUpdate) {
      this.props.onUpdate({
        FirstInvolvement: this.props.FirstInvolvement,
        RecentInvolvement: this.props.RecentInvolvement,
        NatureOfInvolvement: this.props.NatureOfInvolvement,
        Reasons: this.props.Reasons,
        InvolvementWhileEmployed: this.props.InvolvementWhileEmployed,
        InvolvementWithClearance: this.props.InvolvementWithClearance,
        InvolvementInFuture: this.props.InvolvementInFuture,
        HowManyTimes: this.props.HowManyTimes, // PVQ Only
        InvolvementWhileEmployedPosition: this.props.InvolvementWhileEmployedPosition, // PVQ Only
        InvolvementWhileEmployedExplanation: this.props.InvolvementWhileEmployedExplanation, // PVQ Only
        InvolvementWithClearanceExplanation: this.props.InvolvementWithClearanceExplanation, // PVQ Only
        InvolvementInFutureExplanation: this.props.InvolvementInFutureExplanation, // PVQ Only
        InvolvementInFutureGovernment: this.props.InvolvementInFutureGovernment, // PVQ Only
        InvolvementInFutureGovernmentExplanation: this.props.InvolvementInFutureGovernmentExplanation,
        ...values,
      });
    }
  };

  updateFirstInvolvement = (values: SubstanceThcInvolvementItemModel['FirstInvolvement']) => {
    this.update({ FirstInvolvement: values });
  };

  updateRecentInvolvement = (values: SubstanceThcInvolvementItemModel['RecentInvolvement']) => {
    this.update({ RecentInvolvement: values });
  };

  updateNatureOfInvolvement = (
    values: SubstanceThcInvolvementItemModel['NatureOfInvolvement']
  ) => {
    this.update({ NatureOfInvolvement: values });
  };

  updateReasons = (values: SubstanceThcInvolvementItemModel['Reasons']) => {
    this.update({ Reasons: values });
  };

  updateInvolvementWhileEmployed = (
    values: SubstanceThcInvolvementItemModel['InvolvementWhileEmployed']
  ) => {
    this.update({ InvolvementWhileEmployed: values });
  };

  updateInvolvementWithClearance = (
    values: SubstanceThcInvolvementItemModel['InvolvementWithClearance']
  ) => {
    this.update({ InvolvementWithClearance: values });
  };

  updateInvolvementInFuture = (
    values: SubstanceThcInvolvementItemModel['InvolvementInFuture']
  ) => {
    this.update({
      InvolvementInFuture: values,
    });
  };

  updateInvolvementInFutureGovernment = (
    values: SubstanceThcInvolvementItemModel['InvolvementInFutureGovernment']
  ) => {
    this.update({ InvolvementInFutureGovernment: values });
  };

  updateInvolvementInFutureGovernmentExplanation = (
    values: SubstanceThcInvolvementItemModel['InvolvementInFutureGovernmentExplanation']
  ) => {
    this.update({ InvolvementInFutureGovernmentExplanation: values });
  };

  updateHowManyTimes = (values: SubstanceThcInvolvementItemModel['HowManyTimes']) => {
    this.update({ HowManyTimes: values });
  };

  updateInvolvementWhileEmployedPosition = (values: SubstanceThcInvolvementItemModel['InvolvementWhileEmployedPosition']) => {
    this.update({ InvolvementWhileEmployedPosition: values });
  };

  updateInvolvementWhileEmployedExplanation = (values: SubstanceThcInvolvementItemModel['InvolvementWhileEmployedExplanation']) => {
    this.update({ InvolvementWhileEmployedExplanation: values });
  };

  updateInvolvementWithClearanceExplanation = (values: SubstanceThcInvolvementItemModel['InvolvementWithClearanceExplanation']) => {
    this.update({ InvolvementWithClearanceExplanation: values });
  };

  updateInvolvementInFutureExplanation = (values: SubstanceThcInvolvementItemModel['InvolvementInFutureExplanation']) => {
    this.update({ InvolvementInFutureExplanation: values });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;

    return (
      <div className="thc-involvement">
        <Field
          title={i18n.t('substance.pvq.thc.involvement.heading.firstInvolvement')}
          adjustFor="datecontrol"
          attribute={`${attribute}.FirstInvolvement`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateControl
            name="FirstInvolvement"
            attribute={`${attribute}.FirstInvolvement`}
            className="first-involvement"
            {...this.props.FirstInvolvement}
            hideDay
            minDateEqualTo
            onUpdate={this.updateFirstInvolvement}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('substance.pvq.thc.involvement.heading.recentInvolvement')}
          attribute={`${attribute}.RecentInvolvement`}
          adjustFor="datecontrol"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateControl
            {...this.props.RecentInvolvement}
            name="RecentInvolvement"
            attribute={`${attribute}.RecentInvolvement`}
            className="recent-involvement"
            hideDay
            prefix="drugPurchase"
            minDate={this.props.FirstInvolvement}
            minDateEqualTo
            onUpdate={this.updateRecentInvolvement}
            onError={this.props.onError}
            required={this.props.required}
          />

        </Field>
        <Field
          title={i18n.t('substance.pvq.thc.involvement.heading.howManyTimes')}
          attribute={`${attribute}.HowManyTimes`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Text
            {...this.props.HowManyTimes}
            name="HowManyTimes"
            attribute={`${attribute}.HowManyTimes`}
            className="how-many-times"
            onUpdate={this.updateHowManyTimes}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('substance.pvq.thc.involvement.heading.reasons')}
          attribute={`${attribute}.Reasons`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            name="Reasons"
            attribute={`${attribute}.Reasons`}
            className="reasons"
            {...this.props.Reasons}
            onUpdate={this.updateReasons}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
        <Field
          title={i18n.t('substance.pvq.thc.involvement.heading.involvementWithClearance')}
          attribute={`${attribute}.InvolvementWithClearance`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Branch
            name="InvolvementWithClearance"
            attribute={`${attribute}.InvolvementWithClearance`}
            labelSize="h4"
            className="involvement-with-clearance"
            {...this.props.InvolvementWithClearance}
            onError={this.props.onError}
            required={this.props.required}
            onUpdate={this.updateInvolvementWithClearance}
            scrollIntoView={this.props.scrollIntoView}
          />
        </Field>

        {this.props.InvolvementWithClearance?.value === 'Yes' && (
          <Field
            title={i18n.t('substance.pvq.thc.involvement.heading.involvementWithClearanceExplanation')}
            attribute={`${attribute}.InvolvementWithClearanceExplanation`}
            scrollIntoView={this.props.scrollIntoView}
          >
            <Textarea
              {...this.props.InvolvementWithClearanceExplanation}
              name="InvolvementWithClearanceExplanation"
              attribute={`${attribute}.InvolvementWithClearanceExplanation`}
              className="involvementWithClearanceExplanation"
              onUpdate={this.updateInvolvementWithClearanceExplanation}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        )}

        <Branch
          name="InvolvementWhileEmployed"
          attribute={`${attribute}.InvolvementWhileEmployed`}
          label={i18n.t('substance.pvq.thc.involvement.heading.involvementWhileEmployed')}
          labelSize="h4"
          className="involvement-while-employed"
          {...this.props.InvolvementWhileEmployed}
          onError={this.props.onError}
          required={this.props.required}
          onUpdate={this.updateInvolvementWhileEmployed}
          scrollIntoView={this.props.scrollIntoView}
        />
        {this.props.InvolvementWhileEmployed?.value === 'Yes' && (
          <>

            <Field
              title={i18n.t('substance.pvq.thc.involvement.heading.involvementWhileEmployedPosition')}
              attribute={`${attribute}.InvolvementWhileEmployedPosition`}
              scrollIntoView={this.props.scrollIntoView}
              adjustFor="buttons"
              shrink
              className="no-margin-bottom"
            >
              <Dropdown
                attribute={`${attribute}.UseWhileEmployedPosition`}
                name="InvolvementWhileEmployedPosition"
                className="involvementWhileEmployedPosition"
                onUpdate={this.updateInvolvementWhileEmployedPosition}
                onError={this.props.onError}
                value={this.props.InvolvementWhileEmployedPosition?.value}
                required={this.props.required}
              >
                {criminalJusticeorPublicSafetyPosition.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Dropdown>
            </Field>

            <Field
              title={i18n.t('substance.pvq.thc.involvement.heading.involvementWhileEmployedExplanation')}
              attribute={`${attribute}.InvolvementWhileEmployedExplanation`}
              scrollIntoView={this.props.scrollIntoView}
            >
              <Textarea
                {...this.props.InvolvementWhileEmployedExplanation}
                name="involvementWhileEmployedExplanation"
                attribute={`${attribute}.InvolvementWhileEmployedExplanation`}
                className="involvementWhileEmployedExplanation"
                onUpdate={this.updateInvolvementWhileEmployedExplanation}
                onError={this.props.onError}
                required={this.props.required}
              />
            </Field>
          </>
        )}

        <Branch
          name="InvolvementInFuture"
          attribute={`${attribute}.InvolvementInFuture`}
          label={i18n.t('substance.pvq.thc.involvement.heading.involvementInFuture')}
          labelSize="h4"
          className="involvement-in-future"
          {...this.props.InvolvementInFuture}
          onError={this.props.onError}
          required={this.props.required}
          onUpdate={this.updateInvolvementInFuture}
          scrollIntoView={this.props.scrollIntoView}
        />

        <Field
          title={i18n.t('substance.pvq.thc.involvement.heading.involvementInFutureExplanation')}
          attribute={`${attribute}.InvolvementInFutureExplanation`}
          titleSize="label"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            name="InvolvmentInFutureExplanation"
            attribute={`${attribute}.InvolvmentInFutureExplanation`}
            className="involvement-in-future-explanation"
            {...this.props.InvolvementInFutureExplanation}
            onUpdate={this.updateInvolvementInFutureExplanation}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Branch
          name="InvolvementInFutureGovernment"
          attribute={`${attribute}.InvolvementInFutureGovernment`}
          label={i18n.t('substance.pvq.thc.involvement.heading.involvementInFutureGovernment')}
          labelSize="h4"
          className="involvement-in-future-government"
          {...this.props.InvolvementInFutureGovernment}
          onError={this.props.onError}
          required={this.props.required}
          onUpdate={this.updateInvolvementInFutureGovernment}
          scrollIntoView={this.props.scrollIntoView}
        />

        <Field
          title={i18n.t('substance.pvq.thc.involvement.heading.involvementInFutureGovernmentExplanation')}
          attribute={`${attribute}.InvolvementInFutureGovernmentExplanation`}
          titleSize="label"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            name="InvolvementInFutureGovernmentExplanation"
            attribute={`${attribute}InvolvementInFutureGovernmentExplanation`}
            className="involvement-in-future-government-explanation"
            {...this.props.InvolvementInFutureGovernmentExplanation}
            onUpdate={this.updateInvolvementInFutureGovernmentExplanation}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

      </div>
    );
  }
}

ThcInvolvement.defaultProps = {
  FirstInvolvement: {},
  RecentInvolvement: {},
  NatureOfInvolvement: {},
  Reasons: {},
  InvolvementWhileEmployed: {},
  InvolvementWithClearance: {},
  InvolvementInFuture: {},
  HowManyTimes: {},
  InvolvementWhileEmployedPosition: {},
  InvolvementWhileEmployedExplanation: {},
  InvolvementWithClearanceExplanation: {},
  InvolvementInFutureExplanation: {},
  InvolvementInFutureGovernment: {},
  InvolvementInFutureGovernmentExplanation: {},
  onUpdate: (queue) => { },
  onError: (value, arr) => arr,
};
